import {
  ApiTags,
  GetJobResultResponse,
  JobEndpoint,
  JobStatus,
  JobsTypes,
} from "@shared/types";
import {
  JOB_TYPE_ENDPOINTS_MAPPING,
  NO_JOB_ARG_JOB_TYPES,
} from "@shared/services/jobs/constants";
import { EndpointBuilder, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import { jobsActions } from "@shared/slices/jobs.slice";

export const handleGetJobResults = async <
  E extends JobEndpoint,
  Args = JobsTypes[E]["args"],
  Response = JobsTypes[E]["response"]
>(
  endpointName: E,
  baseQuery: any,
  jobId: string,
  args: Args,
  transformResponse?: (
    rawResponse: JobsTypes[E]["rawResponse"],
    args: Args
  ) => Response,
  timeout?: number
): Promise<{ data: Response } | FetchBaseQueryError> => {
  const getJobResultsResponse:
    | { data: GetJobResultResponse }
    | FetchBaseQueryError = await baseQuery({
    url: `get_job_results/`,
    method: "POST",
    body: { job_id: jobId },
  });
  if (getJobResultsResponse && "error" in getJobResultsResponse) {
    return {
      error: getJobResultsResponse.error,
    } as any;
  } else {
    const jobResultsData =
      getJobResultsResponse.data as JobsTypes[E]["rawResponse"];
    const status = jobResultsData.status;
    const { dispatch } = sharedLib.store;
    let parsedData: any;
    if (endpointName === "getJobResultsJob" || status === JobStatus.FINISHED) {
      parsedData = transformResponse
        ? transformResponse(jobResultsData, args)
        : jobResultsData;
      dispatch(
        jobsActions.setJobData({ args, data: parsedData, endpointName })
      );
    }
    if (status === JobStatus.IN_PROGRESS) {
      let finalTimeout = timeout ?? 300;
      if (finalTimeout < 1000) {
        finalTimeout += 200;
      }
      return new Promise(async (resolve) => {
        setTimeout(
          async () =>
            resolve(
              await handleGetJobResults(
                endpointName,
                baseQuery,
                jobId,
                args,
                transformResponse,
                finalTimeout
              )
            ),
          finalTimeout
        );
      });
    } else {
      return { data: parsedData as Response };
    }
  }
};

export const getJobQuery = <
  E extends JobEndpoint,
  Args = JobsTypes[E]["args"],
  Response = JobsTypes[E]["response"]
>({
  build,
  endpointName,
  transformResponse,
  getArgs,
  providesTags,
  keepUnusedDataFor,
  onQueryStarted,
}: {
  build: EndpointBuilder<any, ApiTags, any>;
  endpointName: E;
  transformResponse: (
    rawResponse: JobsTypes[E]["rawResponse"],
    args: Args
  ) => Response;
  getArgs?: (args: Args) => Args;
  providesTags?: any;
  keepUnusedDataFor?: number;
  onQueryStarted?: (arg: Args, api: any) => Promise<void> | void;
}) => {
  return build.query<Response, Args>({
    queryFn: async (args, _, __, baseQuery): Promise<any> => {
      const url = JOB_TYPE_ENDPOINTS_MAPPING[endpointName];
      if (!url) return;
      let finalArgs: any = getArgs ? getArgs(args) : args;
      if (!NO_JOB_ARG_JOB_TYPES.has(endpointName)) {
        if (!finalArgs) finalArgs = { run_as_job: true };
        else finalArgs = { ...finalArgs, run_as_job: true };
      }
      const getJobIdResponse = await baseQuery({
        url,
        method: "POST",
        body: finalArgs,
      });
      const jobId: string | undefined = getJobIdResponse?.data?.data?.job_id;
      if (jobId) {
        return await handleGetJobResults(
          endpointName,
          baseQuery,
          jobId,
          args,
          transformResponse
        );
      }
    },
    providesTags,
    keepUnusedDataFor,
    onQueryStarted,
  });
};
